<!-- Page Header -->
<div class="d-flex justify-content-between align-items-start mb-4">
  <div>
    <nav aria-label="breadcrumb">
      <ol class="breadcrumb mb-1">
        <li class="breadcrumb-item"><a routerLink="/analyses">Analyses</a></li>
        <li class="breadcrumb-item active">Analysis Details</li>
      </ol>
    </nav>
    <div class="d-flex align-items-center gap-3">
      <h1 class="h2 mb-0 fw-bold">{{ analysis()?.fieldName || 'Analysis' }}</h1>
      <span *ngIf="analysis()" class="badge" [ngClass]="getStatusBadgeClass(analysis()!.status)">
        <i [class]="getStatusIcon(analysis()!.status)" class="me-1"></i>
        {{ getStatusText(analysis()!.status) }}
      </span>
    </div>
    <p class="text-muted mb-0 mt-1" *ngIf="analysis()">
      {{ analysis()!.analysisTypeName || 'Analysis' }} &middot; Requested {{ analysis()!.requestedAt | adnDate }}
    </p>
  </div>
  <button class="btn btn-outline-secondary" (click)="goBack()">
    <i class="bi bi-arrow-left me-2"></i>Back
  </button>
</div>

<!-- Loading State -->
<div *ngIf="isLoading()" class="text-center py-5">
  <div class="spinner-border text-primary" role="status">
    <span class="visually-hidden">Loading...</span>
  </div>
  <p class="text-muted mt-2">Loading analysis details...</p>
</div>

<!-- Error State -->
<div *ngIf="!isLoading() && errorMessage()" class="alert alert-danger">
  <i class="bi bi-exclamation-triangle me-2"></i>{{ errorMessage() }}
</div>

<!-- Main Content -->
<div *ngIf="!isLoading() && analysis()" class="row g-4">

  <!-- Left Column -->
  <div class="col-lg-8">

    <!-- Processing Progress -->
    <div *ngIf="isProcessing()" class="card mb-4 border-warning">
      <div class="card-body">
        <div class="d-flex align-items-center gap-3 mb-3">
          <div class="spinner-border text-warning" role="status">
            <span class="visually-hidden">Processing...</span>
          </div>
          <div>
            <h6 class="mb-0">Analysis in Progress</h6>
            <p class="text-muted mb-0 small">Your imagery is being analyzed. This page will update automatically.</p>
          </div>
        </div>
        <div class="progress h-10px">
          <div class="progress-bar progress-bar-striped progress-bar-animated bg-warning"
               [style.width.%]="progress()">
          </div>
        </div>
        <div class="text-muted small mt-1 text-end" *ngIf="progress() > 0">{{ progress() }}% complete</div>
      </div>
    </div>

    <!-- Failed State -->
    <div *ngIf="isFailed()" class="card mb-4 border-danger">
      <div class="card-body">
        <div class="d-flex align-items-center gap-3">
          <i class="bi bi-exclamation-triangle-fill text-danger fs-3"></i>
          <div>
            <h6 class="mb-0">Analysis Failed</h6>
            <p class="text-muted mb-0 small">{{ analysis()!.errorMessage || 'An error occurred during analysis processing.' }}</p>
          </div>
        </div>
      </div>
    </div>

    <!-- Result Files -->
    <div *ngIf="isCompleted()" class="card mb-4">
      <div class="card-header p-3">
        <h5 class="mb-0"><i class="bi bi-file-earmark-check me-2"></i>Results</h5>
      </div>
      <div class="card-body">
        <ng-container *ngIf="hasFiles(); else noFilesYet">
          <!-- PDF Report - Primary Action -->
          <div *ngIf="pdfFiles().length > 0" class="mb-4">
            <h6 class="text-muted small mb-2">Report</h6>
            <div *ngFor="let file of pdfFiles()" class="d-flex align-items-center justify-content-between p-3 rounded mb-2 bg-canvas">
              <div class="d-flex align-items-center gap-3">
                <i class="bi bi-file-earmark-pdf fs-3 text-danger"></i>
                <div>
                  <div class="fw-medium">{{ getFileTypeLabel(file.fileType) }}</div>
                  <div class="text-muted small">
                    <span *ngIf="file.downloadStatus === 'downloaded'" class="text-success">
                      <i class="bi bi-check-circle me-1"></i>Ready to view
                    </span>
                    <span *ngIf="file.downloadStatus === 'pending'" class="text-warning">
                      <i class="bi bi-clock me-1"></i>Preparing...
                    </span>
                    <span *ngIf="file.downloadStatus === 'failed'" class="text-danger">
                      <i class="bi bi-exclamation-triangle me-1"></i>Download failed
                    </span>
                  </div>
                </div>
              </div>
              <button class="btn btn-primary" (click)="viewReport(file)" [disabled]="!file.downloadUrl">
                <i class="bi bi-eye me-2"></i>View Report
              </button>
            </div>
          </div>

          <!-- Raw Data Files - Secondary Actions -->
          <div *ngIf="rawDataFiles().length > 0">
            <h6 class="text-muted small mb-2">Raw Data</h6>
            <div *ngFor="let file of rawDataFiles()" class="d-flex align-items-center justify-content-between p-3 rounded mb-2 bg-canvas">
              <div class="d-flex align-items-center gap-3">
                <i [class]="getFileTypeIcon(file.fileType) + ' fs-3 text-primary'"></i>
                <div>
                  <div class="fw-medium">{{ getFileTypeLabel(file.fileType) }}</div>
                  <div class="text-muted small">
                    <span *ngIf="file.downloadStatus === 'downloaded'" class="text-success">
                      <i class="bi bi-check-circle me-1"></i>Available
                    </span>
                    <span *ngIf="file.downloadStatus === 'pending'" class="text-warning">
                      <i class="bi bi-clock me-1"></i>Preparing...
                    </span>
                    <span *ngIf="file.downloadStatus === 'failed'" class="text-danger">
                      <i class="bi bi-exclamation-triangle me-1"></i>Download failed
                    </span>
                  </div>
                </div>
              </div>
              <button class="btn btn-outline-secondary" (click)="downloadFile(file)" [disabled]="!file.downloadUrl">
                <i class="bi bi-download me-2"></i>Download
              </button>
            </div>
          </div>
        </ng-container>

        <ng-template #noFilesYet>
          <div class="text-center py-4">
            <i class="bi bi-hourglass-split fs-3 text-muted"></i>
            <p class="text-muted mt-2 mb-0">Result files are being prepared. They will appear here shortly.</p>
          </div>
        </ng-template>
      </div>
    </div>

    <!-- Statistics -->
    <div *ngIf="hasStatistics()" class="card mb-4">
      <div class="card-header p-3">
        <h5 class="mb-0"><i class="bi bi-bar-chart me-2"></i>Analysis Statistics</h5>
      </div>
      <div class="card-body">
        <app-agremo-statistics [statistics]="analysis()!.statistics!"></app-agremo-statistics>
      </div>
    </div>

    <!-- Analysis Parameters -->
    <div class="card mb-4">
      <div class="card-header p-3">
        <h5 class="mb-0"><i class="bi bi-sliders me-2"></i>Analysis Parameters</h5>
      </div>
      <div class="card-body">
        <div class="row g-3">
          <div class="col-md-6">
            <label class="form-label text-muted small mb-1">Analysis Type</label>
            <p class="mb-0 fw-medium">{{ analysis()!.analysisTypeName || 'N/A' }}</p>
          </div>
          <div class="col-md-6" *ngIf="analysis()!.analysisParams?.cropType">
            <label class="form-label text-muted small mb-1">Crop Type</label>
            <p class="mb-0 fw-medium">{{ analysis()!.analysisParams!.cropType }}</p>
          </div>
          <div class="col-md-6" *ngIf="analysis()!.analysisParams?.seedingDensity">
            <label class="form-label text-muted small mb-1">Seeding Density</label>
            <p class="mb-0 fw-medium">{{ analysis()!.analysisParams!.seedingDensity | number }} seeds/acre</p>
          </div>
        </div>
      </div>
    </div>
  </div>

  <!-- Right Column -->
  <div class="col-lg-4">
    <!-- Metadata Card -->
    <div class="card mb-4">
      <div class="card-header p-3">
        <h5 class="mb-0"><i class="bi bi-info-circle me-2"></i>Details</h5>
      </div>
      <div class="card-body">
        <div *ngIf="analysis()!.customerName" class="mb-3">
          <label class="form-label text-muted small mb-1">Customer</label>
          <p class="mb-0 fw-medium">
            <ng-container *ngIf="analysis()!.customerId; else customerTextOnly">
              <a [routerLink]="['/customers', analysis()!.customerId, 'view']" class="text-decoration-none">
                <i class="bi bi-person me-1"></i>{{ analysis()!.customerName }}
              </a>
            </ng-container>
            <ng-template #customerTextOnly>
              <i class="bi bi-person me-1"></i>{{ analysis()!.customerName }}
            </ng-template>
          </p>
        </div>
        <div class="mb-3">
          <label class="form-label text-muted small mb-1">Field</label>
          <p class="mb-0 fw-medium">
            <ng-container *ngIf="analysis()!.fieldId; else noFieldLink">
              <a [routerLink]="['/logbook/fields', analysis()!.fieldId, 'edit']" class="text-decoration-none">
                <i class="bi bi-geo-alt me-1"></i>{{ analysis()!.fieldName || 'N/A' }}
              </a>
            </ng-container>
            <ng-template #noFieldLink>{{ analysis()!.fieldName || 'N/A' }}</ng-template>
          </p>
        </div>
        <div class="mb-3">
          <label class="form-label text-muted small mb-1">Status</label>
          <p class="mb-0">
            <span class="badge" [ngClass]="getStatusBadgeClass(analysis()!.status)">
              <i [class]="getStatusIcon(analysis()!.status)" class="me-1"></i>
              {{ getStatusText(analysis()!.status) }}
            </span>
          </p>
        </div>
        <div class="mb-3">
          <label class="form-label text-muted small mb-1">Requested</label>
          <p class="mb-0 fw-medium">{{ analysis()!.requestedAt | adnDate }}</p>
        </div>
        <div *ngIf="analysis()!.completedAt" class="mb-3">
          <label class="form-label text-muted small mb-1">Completed</label>
          <p class="mb-0 fw-medium">{{ analysis()!.completedAt | adnDate }}</p>
        </div>
        <div *ngIf="analysis()!.creditsConsumed" class="mb-0">
          <label class="form-label text-muted small mb-1">Credits Used</label>
          <p class="mb-0 fw-medium">{{ analysis()!.creditsConsumed }}</p>
        </div>
      </div>
    </div>

  </div>
</div>
